import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Clock, ArrowUpRight, ExternalLink } from "lucide-react";

const insights = [
  {
    cat: "Responsible AI",
    title: "The 3 AI Risks Your Board Is Overlooking",
    excerpt: "The biggest AI risk isn't a rogue algorithm—it's an unprepared workforce. Here's what your next board presentation needs to include, based on PwC's Responsible AI framework.",
    time: "6 min read",
    date: "Feb 2025",
    link: "https://www.pwc.com/gx/en/issues/artificial-intelligence/ai-predictions.html",
  },
  {
    cat: "Leadership",
    title: "Why Your AI Strategy Needs a CPA, Not Just a CTO",
    excerpt: "The Intelligence Age demands a new kind of leader—one who speaks the language of both the boardroom and the algorithm. Joe Atkinson on bridging the trust gap.",
    time: "8 min read",
    date: "Jan 2025",
    link: "https://www.pwc.com/gx/en/about/leadership/joe-atkinson.html",
  },
  {
    cat: "Future of Work",
    title: "370,000 People, One AI Transformation: Lessons from the Front Lines",
    excerpt: "Scaling AI across the world's largest professional services firm taught us that technology is the easy part. The hard part is carrying 370,000 people across the bridge.",
    time: "12 min read",
    date: "Dec 2024",
    link: "https://www.pwc.com/gx/en/issues/artificial-intelligence.html",
  },
  {
    cat: "AI Strategy",
    title: "The AI Fitness Index: How to Measure What Actually Matters",
    excerpt: "PwC's proprietary AI Fitness Index reveals that top-performing companies outperform peers by 74% in AI returns. Here's how to benchmark your own organization.",
    time: "10 min read",
    date: "Nov 2024",
    link: "https://www.pwc.com/gx/en/issues/artificial-intelligence/ai-jobs-barometer.html",
  },
  {
    cat: "GenAI",
    title: "ChatGPT Enterprise at Scale: What We Learned Deploying to 370K Users",
    excerpt: "As OpenAI's largest enterprise customer, PwC's deployment of ChatGPT Enterprise across its global workforce is the most ambitious AI rollout in professional services history.",
    time: "9 min read",
    date: "Oct 2024",
    link: "https://www.pwc.com/gx/en/issues/artificial-intelligence.html",
  },
  {
    cat: "Change Management",
    title: "People Are Not Numbers: The Empathy Imperative in AI Transformation",
    excerpt: "Leadership has an obligation to help people learn, develop and apply AI. But obligation without empathy is just mandate—and mandates don't build capability.",
    time: "7 min read",
    date: "Sep 2024",
    link: "https://www.pwc.com/gx/en/about/leadership/joe-atkinson.html",
  },
];

export default function InsightsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section data-testid="insights-section" ref={ref} id="insights" className="py-24 md:py-36 bg-[#1E2229]/25">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="flex items-end justify-between mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-4">Board Briefings</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl leading-tight">
              The latest thinking.
            </h2>
          </div>
          <a
            href="https://www.pwc.com/gx/en/issues/artificial-intelligence.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-all-insights"
            className="hidden md:flex items-center gap-1.5 text-sm text-[#C4963B]/70 hover:text-[#C4963B] transition-colors tracking-wide"
          >
            All insights <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {insights.map((item, i) => (
            <motion.article
              key={i}
              data-testid={`insight-card-${i}`}
              className="group cursor-pointer p-7 md:p-8 bg-[#1E2229] border border-white/[0.04] hover:border-[#C4963B]/25 transition-all duration-500"
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => window.open(item.link, "_blank", "noopener")}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#2A7F7A] font-semibold">
                  {item.cat}
                </span>
                <ArrowUpRight className="w-4 h-4 text-[#E8E6E1]/15 group-hover:text-[#C4963B] transition-colors duration-300" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#F9F6F0] mb-4 group-hover:text-[#C4963B] transition-colors duration-300 leading-snug">
                {item.title}
              </h3>
              <p className="text-sm text-[#E8E6E1]/40 leading-relaxed mb-8">
                {item.excerpt}
              </p>
              <div className="flex items-center gap-4 text-[11px] text-[#E8E6E1]/25">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {item.time}
                </span>
                <span>{item.date}</span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
